import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { atsFetch, AtsEmailAccount, AtsMeeting, timeAgo } from './api';
import { MailboxConnectCard, Modal, SectionCard, inputCls, btnPrimary } from './shared';

// Workspace > Calendar: a real week grid of your synced meetings (Spott-style
// replica of the calendar, not a list), plus outcome prompts for meetings
// that have ended. Logging an outcome writes a meeting note on the person and
// Jev suggests the follow-up from what you wrote.

const HOUR_START = 7;
const HOUR_END = 20;
const PX_PER_HOUR = 48;
const DAY_MS = 86400000;

const mondayOf = (d: Date) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  return x;
};
const fmtTime = (v: string | null) => {
  if (!v) return '';
  return new Date(v).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
};
const sameDay = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const AtsCalendar: React.FC = () => {
  const { token } = useAuth();
  const [configured, setConfigured] = useState(true);
  const [account, setAccount] = useState<AtsEmailAccount | null>(null);
  const [statusLoaded, setStatusLoaded] = useState(false);
  const [adding, setAdding] = useState('');
  const [events, setEvents] = useState<AtsMeeting[]>([]);
  const [awaiting, setAwaiting] = useState<AtsMeeting[]>([]);
  const [weekStart, setWeekStart] = useState<Date>(() => mondayOf(new Date()));
  const [showWeekends, setShowWeekends] = useState(true);
  const [selected, setSelected] = useState<AtsMeeting | null>(null);
  const [outcomes, setOutcomes] = useState<Record<number, string>>({});
  const [confirmation, setConfirmation] = useState('');

  useEffect(() => {
    if (!token) return;
    atsFetch(token, '/email/status')
      .then((d) => { setConfigured(d.configured); setAccount(d.account); })
      .catch(() => {})
      .finally(() => setStatusLoaded(true));
  }, [token]);

  const addAttendee = async (m: AtsMeeting, a: { address: string; name: string | null }, as: 'contact' | 'candidate') => {
    if (!token || adding) return;
    setAdding(a.address);
    try {
      const d = await atsFetch(token, '/calendar/attendees/add', {
        method: 'POST',
        body: JSON.stringify({ meetingId: m.id, address: a.address, name: a.name, as }),
      });
      setConfirmation(d.client
        ? `Added ${d.person.name} as a contact at ${d.client.name}.`
        : `Added ${d.person.name} as a ${as}.`);
      setTimeout(() => setConfirmation(''), 6000);
      load();
      setSelected((prev) => prev && prev.id === m.id
        ? { ...prev, person_id: prev.person_id || d.person.id, attendees: (prev.attendees || []).map((x) => x.address === a.address ? { ...x, personId: d.person.id } : x) }
        : prev);
    } catch { /* confirmation stays empty */ }
    setAdding('');
  };

  const load = useCallback(() => {
    if (!token || !account) return;
    const from = weekStart.toISOString();
    const to = new Date(weekStart.getTime() + 7 * DAY_MS).toISOString();
    atsFetch(token, `/calendar?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`)
      .then((d) => { setEvents(d.events); setAwaiting(d.awaitingOutcome); })
      .catch(() => {});
  }, [token, account, weekStart]);

  useEffect(load, [load]);

  const logOutcome = async (m: AtsMeeting) => {
    if (!token) return;
    const body = (outcomes[m.id] || '').trim();
    try {
      const d = await atsFetch(token, `/calendar/${m.id}/outcome`, { method: 'POST', body: JSON.stringify({ body }) });
      setConfirmation(d.suggestion ? `Logged — Jev suggested: "${d.suggestion.title}".` : 'Outcome logged.');
      setOutcomes((prev) => ({ ...prev, [m.id]: '' }));
      load();
      setTimeout(() => setConfirmation(''), 6000);
    } catch { /* keep text */ }
  };

  const skip = async (m: AtsMeeting) => {
    if (!token) return;
    await atsFetch(token, `/calendar/${m.id}/skip`, { method: 'POST' }).catch(() => {});
    load();
  };

  const days = useMemo(() => {
    const all = Array.from({ length: 7 }, (_, i) => new Date(weekStart.getTime() + i * DAY_MS));
    return showWeekends ? all : all.slice(0, 5);
  }, [weekStart, showWeekends]);

  const eventsByDay = useMemo(() => {
    const map = new Map<number, AtsMeeting[]>();
    for (const ev of events) {
      if (!ev.starts_at) continue;
      const d = new Date(ev.starts_at);
      const idx = days.findIndex((day) => sameDay(day, d));
      if (idx === -1) continue;
      if (!map.has(idx)) map.set(idx, []);
      map.get(idx)!.push(ev);
    }
    return map;
  }, [events, days]);

  if (!statusLoaded) return <div className="py-16 text-center text-gray-400 text-sm">Loading…</div>;
  if (!account) return <MailboxConnectCard configured={configured} />;

  const now = new Date();
  const gridHeight = (HOUR_END - HOUR_START) * PX_PER_HOUR;
  const weekLabel = `${days[0].toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} – ${days[days.length - 1].toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;

  return (
    <div className="space-y-5">
      {confirmation && <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">{confirmation}</p>}

      {awaiting.length > 0 && (
        <SectionCard title={`Needs an outcome (${awaiting.length})`}>
          <div className="space-y-4">
            {awaiting.map((m) => (
              <div key={m.id} className="rounded-lg border border-amber-200 bg-amber-50/50 px-4 py-3">
                <p className="text-sm font-medium text-gray-900">
                  {m.subject || 'Meeting'}
                  {m.person_id && <> — <Link to={`/ats/people/${m.person_id}`} className="text-blue-700 hover:underline">{m.person_name}</Link></>}
                </p>
                <p className="text-[11px] text-gray-500 mb-2">Ended {timeAgo(m.ends_at)}</p>
                <div className="flex gap-2">
                  <input
                    value={outcomes[m.id] || ''}
                    onChange={(e) => setOutcomes((prev) => ({ ...prev, [m.id]: e.target.value }))}
                    onKeyDown={(e) => { if (e.key === 'Enter') logOutcome(m); }}
                    placeholder="What happened? e.g. went well — they want two CVs by Friday"
                    className={inputCls}
                  />
                  <button onClick={() => logOutcome(m)} className={btnPrimary}>Log</button>
                  <button onClick={() => skip(m)} className="px-2 text-xs text-gray-400 hover:text-gray-600">Skip</button>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {/* Week navigation */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1">
          <button onClick={() => setWeekStart(new Date(weekStart.getTime() - 7 * DAY_MS))} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500" title="Previous week">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
          </button>
          <button onClick={() => setWeekStart(mondayOf(new Date()))} className="px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200">Today</button>
          <button onClick={() => setWeekStart(new Date(weekStart.getTime() + 7 * DAY_MS))} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500" title="Next week">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
          </button>
        </div>
        <span className="text-sm font-semibold text-gray-800">{weekLabel}</span>
        <label className="flex items-center gap-1.5 text-xs text-gray-500 ml-auto cursor-pointer">
          <input type="checkbox" checked={showWeekends} onChange={(e) => setShowWeekends(e.target.checked)} className="rounded border-gray-300" />
          Weekends
        </label>
        <span className="text-xs text-gray-400">Meetings with your ATS people sync from {account.address}</span>
      </div>

      {/* Week grid */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="grid" style={{ gridTemplateColumns: `52px repeat(${days.length}, 1fr)` }}>
          <div className="border-b border-gray-100" />
          {days.map((d, i) => {
            const isToday = sameDay(d, now);
            return (
              <div key={i} className={`border-b border-l border-gray-100 px-2 py-2 text-center ${isToday ? 'bg-emerald-50/60' : ''}`}>
                <p className={`text-[10px] font-semibold uppercase tracking-wide ${isToday ? 'text-emerald-600' : 'text-gray-400'}`}>
                  {d.toLocaleDateString('en-GB', { weekday: 'short' })}
                </p>
                <p className={`text-sm font-semibold ${isToday ? 'text-emerald-700' : 'text-gray-700'}`}>{d.getDate()}</p>
              </div>
            );
          })}
        </div>
        <div className="grid overflow-y-auto" style={{ gridTemplateColumns: `52px repeat(${days.length}, 1fr)`, maxHeight: '58vh' }}>
          {/* Hour labels */}
          <div className="relative" style={{ height: gridHeight }}>
            {Array.from({ length: HOUR_END - HOUR_START }, (_, i) => (
              <span key={i} className="absolute right-1.5 text-[10px] text-gray-300 -translate-y-1/2" style={{ top: i * PX_PER_HOUR }}>
                {i > 0 ? `${HOUR_START + i}:00` : ''}
              </span>
            ))}
          </div>
          {days.map((d, dayIdx) => {
            const isToday = sameDay(d, now);
            const nowTop = isToday ? ((now.getHours() + now.getMinutes() / 60 - HOUR_START) / (HOUR_END - HOUR_START)) * gridHeight : null;
            return (
              <div key={dayIdx} className={`relative border-l border-gray-100 ${isToday ? 'bg-emerald-50/30' : ''}`} style={{ height: gridHeight }}>
                {Array.from({ length: HOUR_END - HOUR_START }, (_, i) => (
                  <div key={i} className="absolute left-0 right-0 border-t border-gray-50" style={{ top: i * PX_PER_HOUR }} />
                ))}
                {nowTop !== null && nowTop >= 0 && nowTop <= gridHeight && (
                  <div className="absolute left-0 right-0 z-10 pointer-events-none" style={{ top: nowTop }}>
                    <div className="h-px bg-red-400" />
                    <span className="absolute -left-0.5 -top-[3px] w-1.5 h-1.5 rounded-full bg-red-400" />
                  </div>
                )}
                {(eventsByDay.get(dayIdx) || []).map((ev) => {
                  const s = new Date(ev.starts_at!);
                  const e = ev.ends_at ? new Date(ev.ends_at) : new Date(s.getTime() + 30 * 60000);
                  const startH = Math.max(HOUR_START, s.getHours() + s.getMinutes() / 60);
                  const endH = Math.min(HOUR_END, Math.max(startH + 0.4, e.getHours() + e.getMinutes() / 60));
                  const top = ((startH - HOUR_START) / (HOUR_END - HOUR_START)) * gridHeight;
                  const height = Math.max(22, ((endH - startH) / (HOUR_END - HOUR_START)) * gridHeight - 2);
                  const past = e < now;
                  return (
                    <button
                      key={ev.id}
                      onClick={() => setSelected(selected?.id === ev.id ? null : ev)}
                      className={`absolute left-0.5 right-0.5 rounded-md px-1.5 py-1 text-left overflow-hidden border z-[5] ${
                        selected?.id === ev.id
                          ? 'bg-emerald-600 border-emerald-700 text-white'
                          : past
                            ? 'bg-gray-50 border-gray-200 text-gray-400'
                            : 'bg-emerald-50 border-emerald-200 text-emerald-900 hover:bg-emerald-100'
                      }`}
                      style={{ top, height }}
                    >
                      <p className="text-[10px] font-semibold leading-tight truncate">{fmtTime(ev.starts_at)} {ev.subject || 'Meeting'}</p>
                      {height > 34 && ev.person_name && <p className="text-[10px] leading-tight truncate opacity-80">{ev.person_name}</p>}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      {/* Selected event — popup modal (the invite itself) */}
      {selected && (
        <Modal title={selected.subject || 'Meeting'} onClose={() => setSelected(null)}>
          <p className="text-sm text-gray-600 mb-1">
            {selected.starts_at && new Date(selected.starts_at).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
            {' · '}{fmtTime(selected.starts_at)}–{fmtTime(selected.ends_at)}
          </p>
          {selected.location && <p className="text-xs text-gray-400 mb-3">{selected.location}</p>}

          {(selected.attendees || []).length > 0 && (
            <div className="mt-3">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-2">Attendees</p>
              <div className="space-y-2">
                {(selected.attendees || []).map((a) => (
                  <div key={a.address} className="flex items-center gap-2 text-sm">
                    {a.personId ? (
                      <Link to={`/ats/people/${a.personId}`} className="text-blue-700 hover:underline font-medium">{a.name || a.address} ↗</Link>
                    ) : (
                      <>
                        <span className="text-gray-700 truncate">{a.name || a.address}</span>
                        <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-amber-50 text-amber-700 uppercase flex-shrink-0">Not in ATS</span>
                        <span className="ml-auto flex gap-1.5 flex-shrink-0">
                          <button
                            onClick={() => addAttendee(selected, a, 'contact')}
                            disabled={adding === a.address}
                            className="px-2 py-1 rounded-md bg-violet-600 text-white text-[11px] font-medium hover:bg-violet-700 disabled:opacity-50"
                          >
                            {adding === a.address ? '…' : '+ Contact'}
                          </button>
                          <button
                            onClick={() => addAttendee(selected, a, 'candidate')}
                            disabled={adding === a.address}
                            className="px-2 py-1 rounded-md bg-blue-600 text-white text-[11px] font-medium hover:bg-blue-700 disabled:opacity-50"
                          >
                            + Candidate
                          </button>
                        </span>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-5">
            <button onClick={() => setSelected(null)} className="px-3 py-2 rounded-lg text-sm font-medium text-gray-500 hover:bg-gray-100">Close</button>
            {selected.online_url && (
              <a href={selected.online_url} target="_blank" rel="noreferrer" className="px-3.5 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700">Join ↗</a>
            )}
          </div>
        </Modal>
      )}

    </div>
  );
};

export default AtsCalendar;
